import { z } from 'zod';

import { parseOrThrow } from '../../api';
import type { PlantProfile } from '../types';

const nullableText = z.string().nullable().default(null);

const blankToNull = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
};

const plantProfileSchema = z.object({
  plantId: z.string(),
  name: z.string(),
  code: z.string(),
  address: nullableText,
  capacityKld: z.number().nonnegative().nullable().default(null),
  timezone: nullableText,
});

const legacyPlantSchema = z.object({
  _id: z.string().min(1),
  plantName: z
    .string()
    .nullish()
    .transform((name) => name ?? ''),
  abbr: z.string().nullish(),
  location: z.object({ place: z.string().nullish() }).nullish().catch(null),
  capacityKld: z.number().nonnegative().nullish().catch(null),
  timezone: z.string().nullish().catch(null),
});

const plantDetailsSchema = z.object({ result: legacyPlantSchema.nullable() }).transform(
  ({ result }): PlantProfile | null =>
    result === null
      ? null
      : {
          plantId: result._id,
          name: result.plantName,
          code: result.abbr ?? '',
          address: blankToNull(result.location?.place),
          capacityKld: result.capacityKld ?? null,
          timezone: blankToNull(result.timezone),
        },
);

const plantNameSchema = z
  .object({ result: z.object({ name: z.string().nullish() }) })
  .transform(({ result }): string | null => blankToNull(result.name));

export const parsePlantProfile = (raw: unknown): PlantProfile => parseOrThrow(plantProfileSchema, raw, 'plant profile');

export const parsePlantDetails = (raw: unknown): PlantProfile | null => parseOrThrow(plantDetailsSchema, raw, 'plant details');

export const parsePlantName = (raw: unknown): string | null => parseOrThrow(plantNameSchema, raw, 'plant name');
